import { Loader } from "@/components/Common/Loader/Loader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { AnalyticsEventType } from "@/constants/analytics";
import { useCaptchaToken } from "@/hooks/useCaptchaToken";
import { useNotificationStore } from "@/store/notification";
import { trackEvent } from "@/utils/analytics";
import { emailSchema, REQUIRED_MESSAGE } from "@/utils/form";
import { sendCode } from "@/utils/passwordless";
import { Formik } from "formik";
import ReCAPTCHA from "react-google-recaptcha";
import * as Yup from "yup";
import env from "@/env";

type OTPEmailStepProps = {
  hasBackButton?: boolean;
  onCancel: () => void;
  onComplete?: (options: { email: string }) => void;
};

export const OTPEmailStep = ({ onComplete, onCancel, hasBackButton }: OTPEmailStepProps) => {
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();
  const {
    captchaResetCounter,
    isCaptchaComplete,
    isCaptchaRequired,
    getCaptchaToken,
    resetCaptcha,
    setCaptchaToken
  } = useCaptchaToken();

  return (
    <Formik<OTPEmailFormType>
      initialValues={{
        email: ""
      }}
      validationSchema={OTPEmailFormTypeFormSchema}
      onSubmit={async ({ email }) => {
        try {
          const captcha = await getCaptchaToken();
          await sendCode({ email, captcha });

          toast({ title: "OTP sent. Please check your inbox.", variant: "success" });

          trackEvent(AnalyticsEventType.OTP_REQUESTED_CODE_COMPLETED);

          onComplete?.({ email });
        } catch (e) {
          trackEvent(AnalyticsEventType.OTP_REQUESTED_CODE_FAILED);

          displayError({
            title: "ERROR",
            message: (e as Error).message || "Failed to send code",
            isPublic: true
          });
        } finally {
          // Each token is single-use on server verification.
          resetCaptcha();
        }
      }}
      validateOnMount={true}
    >
      {({
        handleSubmit,
        handleBlur,
        handleChange,
        values,
        isValid,
        isSubmitting,
        touched,
        errors
      }) => {
        return (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>Enter Email</Label>
              <Input
                name="email"
                value={values.email}
                invalid={touched.email && !!errors.email}
                onBlur={handleBlur}
                onChange={handleChange}
              />
              {touched.email && errors.email && (
                <div className="subtext-small capitalize text-red-light-500">{errors.email}</div>
              )}
            </div>

            <div className="flex flex-row items-center gap-3">
              {hasBackButton && (
                <Button
                  type="button"
                  variant={"outline"}
                  className="flex-1 gap-3 bg-gray-dark-600"
                  onClick={onCancel}
                >
                  <span>Go back</span>
                </Button>
              )}
              <Button
                type="submit"
                variant={"outline"}
                className="flex-1 gap-3 bg-gray-dark-600"
                disabled={!isValid || isSubmitting || (isCaptchaRequired && !isCaptchaComplete)}
              >
                <span>Continue</span>
                {isSubmitting && <Loader className="!h-[14px] !w-[14px]" color="#fff" />}
              </Button>
            </div>

            {isCaptchaRequired && (
              <div className="flex justify-center">
                <ReCAPTCHA
                  key={captchaResetCounter}
                  sitekey={env.RECAPTCHA_V2_SITE_KEY}
                  onChange={(token: string | null) => setCaptchaToken(token)}
                  onExpired={() => setCaptchaToken(null)}
                  onErrored={() => setCaptchaToken(null)}
                />
              </div>
            )}

            {/* <p className="subtext-small text-gray-dark-1000">
                        <a
                            onClick={onCancel}
                            className="cursor-pointer text-foreground hover:underline"
                        >
                            Cancel
                        </a>
                    </p> */}
          </form>
        );
      }}
    </Formik>
  );
};

export type OTPEmailFormType = {
  email: string;
};

const OTPEmailFormTypeFormSchema = Yup.object().shape({
  email: emailSchema().required(REQUIRED_MESSAGE)
});
